import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useEditor } from '../../../../../../../App.jsx';
import { showAlert, showErrorAlert } from '../../../../../../../components/HelperAlerts/HelpersAlerts.jsx';
import { getThemeContext } from '../../../../../../../components/providers/ThemeProvider.jsx';
import { FadeIn } from '../../../../../../../components/ui/index.js';
import { ArrowLeft, Link2, Clock, Hash, Plus, Save, Palette } from 'lucide-react';
import SearchPicker from '../../../../../../../components/admin/SearchPicker.jsx';

const defaultExpiresAt = () => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const createInitialTokenState = () => ({
    token: '',
    expires_at: defaultExpiresAt(),
    max_usage: '0',
    model_3d: '',
    allow_color_edit: true,
});

const formatApiError = (err, fallback) => {
    const data = err.response?.data;
    if (!data) return fallback;
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    return Object.entries(data)
        .map(([field, value]) => `${field}: ${Array.isArray(value) ? value.join(', ') : value}`)
        .join(' | ');
};

const convertLocalDatetimeToUTC = (localDatetimeStr) => {
    if (!localDatetimeStr) return '';
    const [datePart, timePart] = localDatetimeStr.split('T');
    if (!datePart || !timePart) return '';
    const [year, month, day]  = datePart.split('-').map(Number);
    const [hour, minute]      = timePart.split(':').map(Number);
    return new Date(year, month - 1, day, hour, minute).toISOString();
};

const getExpirationSeconds = (localDatetimeStr) => {
    if (!localDatetimeStr) return 0;
    const expiresAt = new Date(convertLocalDatetimeToUTC(localDatetimeStr));
    return Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / 1000));
};

const formatDatetimeLocal = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const TokenLinkEditor = ({ editorID }) => {
    const [tokenLink, setTokenLink] = useState(createInitialTokenState);
    const [errors, setErrors]       = useState({});
    const [loading, setLoading]     = useState(false);
    const [selectedModelLabel, setSelectedModelLabel] = useState('');
    const { stateEditor, setStateEditor } = useEditor();
    const theme = getThemeContext();
    const isEdit = editorID && editorID !== 'add';

    useEffect(() => {
        if (isEdit) {
            axios.get(`/public-tokens/${editorID}/`)
                .then(res => {
                    const token = res.data;
                    setTokenLink({
                        token: token.token || '',
                        expires_at: token.expires_at || '',
                        max_usage: String(token.max_usage ?? 0),
                        model_3d: token.model_3d?.id || '',
                        allow_color_edit: token.allow_color_edit ?? true,
                    });
                    setSelectedModelLabel(token.model_3d ? `#${token.model_3d.id} - ${token.model_3d.name}` : '');
                })
                .catch((err) => showErrorAlert({ title: 'Error', text: formatApiError(err, 'No se pudo cargar el enlace.'), theme }));
        }
    }, [editorID]);

    const handleBack = () => {
        setStateEditor({ ...stateEditor, editorType: null, editorID: null, editor: null, profile: 'enlaces_publicos' });
    };

    const validate = () => {
        const e = {};
        if (!tokenLink.expires_at) e.expires_at = 'La fecha de expiración es obligatoria.';
        else if (getExpirationSeconds(tokenLink.expires_at) <= 0) e.expires_at = 'La fecha debe estar en el futuro.';
        if (!tokenLink.max_usage || isNaN(tokenLink.max_usage)) e.max_usage = 'Debe ser un número válido.';
        if (!String(tokenLink.model_3d).trim()) e.model_3d = 'El ID del modelo 3D es obligatorio.';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setTokenLink(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);

        try {
            if (!isEdit) {
                const { data } = await axios.post(`/generate-public-token/${tokenLink.model_3d}/`, {
                    expiration_seconds: getExpirationSeconds(tokenLink.expires_at),
                    max_usage: Number(tokenLink.max_usage || 0),
                    allow_color_edit: tokenLink.allow_color_edit,
                });
                const publicUrl = data.public_url
                    ? `${window.location.origin}/${String(data.public_url).replace(/^\/+/, '')}`
                    : '';
                if (publicUrl) await navigator.clipboard.writeText(publicUrl);
                const result = await showAlert({
                    title: 'Enlace creado',
                    text: publicUrl ? `Copiado al portapapeles: ${publicUrl}` : 'El enlace se ha creado correctamente.',
                    icon: 'success',
                    showCancelButton: true,
                    showDenyButton: true,
                    confirmButtonText: 'Volver a la lista',
                    denyButtonText: 'Añadir otro',
                    cancelButtonText: 'Quedarme aquí',
                    theme,
                });
                if (result.isConfirmed) handleBack();
                else if (result.isDenied) {
                    setTokenLink(createInitialTokenState());
                    setSelectedModelLabel('');
                }
            } else {
                await axios.put(`/public-tokens/${editorID}/`, {
                    expires_at: convertLocalDatetimeToUTC(tokenLink.expires_at),
                    max_usage: Number(tokenLink.max_usage || 0),
                    allow_color_edit: tokenLink.allow_color_edit,
                });
                const result = await showAlert({
                    title: 'Enlace actualizado',
                    icon: 'success',
                    showCancelButton: true,
                    confirmButtonText: 'Volver a la lista',
                    cancelButtonText: 'Quedarme aquí',
                    theme,
                });
                if (result.isConfirmed) handleBack();
            }
        } catch (err) {
            showErrorAlert({
                title: isEdit ? 'No se pudo actualizar' : 'No se pudo crear',
                text: formatApiError(err, `No se pudo ${isEdit ? 'modificar' : 'crear'} el enlace.`),
                theme,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full h-full overflow-y-auto scrollbar_mod flex flex-col items-center justify-start p-4 gap-4">

            {/* Back button */}
            <div className="w-full max-w-2xl">
                <button onClick={handleBack} className="btn btn-ghost btn-sm gap-2">
                    <ArrowLeft size={16} /> Volver
                </button>
            </div>

            <FadeIn className="w-full max-w-2xl">
                <div className="glassCard rounded-2xl shadow-xl">
                    <div className="card-body p-6 gap-6">

                        {/* Header */}
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
                                <Link2 size={22} />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-base-content">
                                    {isEdit ? 'Editar enlace público' : 'Nuevo enlace público'}
                                </h2>
                                <p className="text-sm text-base-content/50">
                                    {isEdit ? 'Modifica los parámetros del enlace' : 'Controla el acceso a un modelo 3D'}
                                </p>
                            </div>
                        </div>

                        <div className="divider my-0" />

                        <form onSubmit={handleSubmit} className="flex flex-col gap-5">

                            {/* Token (read-only in edit mode) */}
                            {isEdit && (
                                <div className="form-control gap-1">
                                    <label className="label py-0">
                                        <span className="label-text font-semibold flex items-center gap-1.5">
                                            <Hash size={14} className="text-base-content/50" /> Token
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        value={tokenLink.token || ''}
                                        readOnly
                                        className="input input-bordered w-full font-mono text-sm bg-base-200 cursor-default"
                                    />
                                    <label className="label py-0">
                                        <span className="label-text-alt text-base-content/40">Solo lectura</span>
                                    </label>
                                </div>
                            )}

                            {/* Fecha de expiración */}
                            <div className="form-control gap-1">
                                <label className="label py-0">
                                    <span className="label-text font-semibold flex items-center gap-1.5">
                                        <Clock size={14} className="text-base-content/50" /> Fecha de expiración
                                    </span>
                                </label>
                                <input
                                    type="datetime-local"
                                    name="expires_at"
                                    value={formatDatetimeLocal(tokenLink.expires_at)}
                                    onChange={handleChange}
                                    className={`input input-bordered w-full ${errors.expires_at ? 'input-error' : ''}`}
                                />
                                {errors.expires_at && (
                                    <label className="label py-0">
                                        <span className="label-text-alt text-error">{errors.expires_at}</span>
                                    </label>
                                )}
                            </div>

                            {/* Uso máximo */}
                            <div className="form-control gap-1">
                                <label className="label py-0">
                                    <span className="label-text font-semibold flex items-center gap-1.5">
                                        <Hash size={14} className="text-base-content/50" /> Uso máximo
                                    </span>
                                    <span className="label-text-alt text-base-content/40">0 = ilimitado</span>
                                </label>
                                <input
                                    type="number"
                                    name="max_usage"
                                    min={0}
                                    value={tokenLink.max_usage}
                                    onChange={handleChange}
                                    placeholder="0 para uso ilimitado"
                                    className={`input input-bordered w-full ${errors.max_usage ? 'input-error' : ''}`}
                                />
                                {errors.max_usage && (
                                    <label className="label py-0">
                                        <span className="label-text-alt text-error">{errors.max_usage}</span>
                                    </label>
                                )}
                            </div>

                            {/* Modelo 3D */}
                            <div className="form-control gap-1">
                                <SearchPicker
                                    label="Modelo 3D"
                                    endpoint="/customized-keyrings-admin/"
                                    value={tokenLink.model_3d}
                                    selectedLabel={selectedModelLabel}
                                    placeholder="Buscar modelo por nombre"
                                    emptyText="No hay modelos con esa búsqueda."
                                    disabled={isEdit}
                                    getOptionLabel={(option) => `#${option.id} - ${option.name}`}
                                    getOptionDescription={(option) => option.usuario ? `Usuario #${option.usuario}` : 'Modelo guardado'}
                                    getOptionThumbnail={(option) => option.path_thumbnail}
                                    onSelect={(option) => {
                                        setTokenLink(prev => ({ ...prev, model_3d: option?.id || '' }));
                                        setSelectedModelLabel(option ? `#${option.id} - ${option.name}` : '');
                                        if (errors.model_3d) setErrors(prev => ({ ...prev, model_3d: undefined }));
                                    }}
                                />
                                {errors.model_3d && (
                                    <label className="label py-0">
                                        <span className="label-text-alt text-error">{errors.model_3d}</span>
                                    </label>
                                )}
                            </div>

                            {/* Permitir modificar colores */}
                            <div className="form-control gap-1">
                                <label className="label cursor-pointer justify-start gap-3 py-0">
                                    <input
                                        type="checkbox"
                                        name="allow_color_edit"
                                        checked={tokenLink.allow_color_edit}
                                        onChange={handleChange}
                                        className="checkbox checkbox-primary"
                                    />
                                    <span className="label-text font-semibold flex items-center gap-1.5">
                                        <Palette size={14} className="text-base-content/50" /> Permitir modificar colores
                                    </span>
                                </label>
                                <span className="label-text-alt text-base-content/40 pl-9">
                                    Los visitantes del enlace podrán previsualizar el modelo con otros colores.
                                </span>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn btn-primary flex-1 gap-2"
                                >
                                    {loading
                                        ? <span className="loading loading-spinner loading-sm" />
                                        : isEdit
                                            ? <><Save size={16} /> Guardar cambios</>
                                            : <><Plus size={16} /> Crear enlace</>
                                    }
                                </button>
                                <button type="button" onClick={handleBack} className="btn btn-ghost gap-2">
                                    <ArrowLeft size={16} /> Cancelar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </FadeIn>
        </div>
    );
};

export default TokenLinkEditor;
